import { useState } from 'react';
import { api } from '../lib/api.ts';
import { useAsync } from '../lib/useAsync.ts';
import { ROLE_LABEL, useAuth } from '../lib/auth.tsx';
import { ErrorState, Field, Loading, Modal, PageHeader } from '../components/ui.tsx';
import { formatDate, formatDateTime } from '../lib/format.ts';
import type { PublicUser, Role, UserHistoryEntry } from '../../../shared/types.ts';

const ROLES: Role[] = ['superadmin', 'gerente', 'leitor'];

const HISTORY_FIELD_LABEL: Record<string, string> = {
  name: 'Nome',
  email: 'E-mail',
  role: 'Papel',
  active: 'Situação',
  username: 'Usuário',
  google_email: 'Conta Google',
  password: 'Senha',
};

function historyValueLabel(field: string, value: string | null): string {
  if (field === 'active') return value === 'true' ? 'Ativo' : 'Desativado';
  if (field === 'role') return ROLE_LABEL[value as Role] ?? value ?? '—';
  if (value === null) return '—';
  return value;
}

export function UsersPage() {
  const { user: me } = useAuth();
  const users = useAsync(() => api.get<PublicUser[]>('/users'), []);
  const [creating, setCreating] = useState(false);
  const [editing, setEditing] = useState<PublicUser | null>(null);
  const [viewingHistory, setViewingHistory] = useState<PublicUser | null>(null);
  const [error, setError] = useState<string | null>(null);

  async function update(target: PublicUser, changes: Partial<PublicUser>) {
    setError(null);
    try {
      await api.patch(`/users/${target.id}`, changes);
      users.reload();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Não foi possível atualizar.');
    }
  }

  if (users.loading) return <Loading label="Carregando usuários…" />;
  if (users.error) return <ErrorState message={users.error} onRetry={users.reload} />;

  return (
    <>
      <PageHeader
        title="Usuários"
        subtitle="Superadmin administra a plataforma. Gerentes criam e movem ações. Leitores só consultam."
        actions={
          <button type="button" className="btn btn-primary" onClick={() => setCreating(true)}>
            Novo usuário
          </button>
        }
      />

      {error && <ErrorState message={error} />}

      <section className="card table-wrap">
        <table className="table">
          <thead>
            <tr>
              <th>Nome</th>
              <th>Login</th>
              <th>Papel</th>
              <th>Situação</th>
              <th>Desde</th>
              <th className="align-right">Ações</th>
            </tr>
          </thead>
          <tbody>
            {(users.data ?? []).map((user) => {
              const isMe = user.id === me?.id;
              return (
                <tr key={user.id} className={user.active ? '' : 'row--muted'}>
                  <td>
                    {user.name}
                    {isMe && <span className="text-micro text-muted"> (você)</span>}
                  </td>
                  <td className="text-muted">
                    <div>{user.email}</div>
                    <div className="text-micro">
                      {user.username && <span className="badge badge-status-neutral">@{user.username}</span>}{' '}
                      {user.googleEmail && <span className="badge badge-status-neutral">Google</span>}
                    </div>
                  </td>
                  <td>
                    <select
                      className="select select--sm"
                      value={user.role}
                      disabled={isMe}
                      onChange={(e) => void update(user, { role: e.target.value as Role })}
                      aria-label={`Papel de ${user.name}`}
                    >
                      {ROLES.map((role) => (
                        <option key={role} value={role}>
                          {ROLE_LABEL[role]}
                        </option>
                      ))}
                    </select>
                  </td>
                  <td>
                    <span
                      className={`badge ${user.active ? 'badge-status-success' : 'badge-status-neutral'}`}
                    >
                      {user.active ? 'Ativo' : 'Desativado'}
                    </span>
                  </td>
                  <td className="text-muted tabular-nums">{formatDate(user.createdAt)}</td>
                  <td className="align-right">
                    <div className="table-actions">
                      <button
                        type="button"
                        className="btn btn-ghost btn-sm"
                        onClick={() => setEditing(user)}
                      >
                        Editar
                      </button>
                      <button
                        type="button"
                        className="btn btn-ghost btn-sm"
                        onClick={() => setViewingHistory(user)}
                      >
                        Histórico
                      </button>
                      <button
                        type="button"
                        className={`btn btn-sm ${user.active ? 'btn-ghost' : 'btn-secondary'}`}
                        disabled={isMe}
                        onClick={() => void update(user, { active: !user.active })}
                      >
                        {user.active ? 'Desativar' : 'Reativar'}
                      </button>
                    </div>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </section>

      <p className="text-micro text-muted">
        Um superadmin não pode rebaixar nem desativar a si mesmo — peça a outro superadmin. Trocar a
        senha, mudar o papel, e-mail/usuário ou desativar alguém encerra as sessões abertas dessa pessoa
        na hora.
      </p>

      {creating && (
        <UserForm
          onClose={() => setCreating(false)}
          onSaved={() => {
            setCreating(false);
            users.reload();
          }}
        />
      )}

      {editing && (
        <EditUserForm
          user={editing}
          onClose={() => setEditing(null)}
          onSaved={() => {
            setEditing(null);
            users.reload();
          }}
        />
      )}

      {viewingHistory && (
        <UserHistoryModal user={viewingHistory} onClose={() => setViewingHistory(null)} />
      )}
    </>
  );
}

function UserForm({ onClose, onSaved }: { onClose: () => void; onSaved: () => void }) {
  const [form, setForm] = useState({
    name: '',
    email: '',
    username: '',
    password: '',
    role: 'gerente' as Role,
  });
  const [error, setError] = useState<string | null>(null);
  const [saving, setSaving] = useState(false);

  async function save() {
    setSaving(true);
    setError(null);
    try {
      await api.post('/users', { ...form, username: form.username.trim() || null });
      onSaved();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Não foi possível criar o usuário.');
      setSaving(false);
    }
  }

  return (
    <Modal
      title="Novo usuário"
      onClose={onClose}
      footer={
        <>
          <button type="button" className="btn btn-ghost" onClick={onClose}>
            Cancelar
          </button>
          <button
            type="button"
            className="btn btn-primary"
            onClick={() => void save()}
            disabled={saving}
          >
            {saving ? 'Criando…' : 'Criar usuário'}
          </button>
        </>
      }
    >
      <div className="stack">
        <Field label="Nome">
          <input
            className="input"
            value={form.name}
            onChange={(e) => setForm((f) => ({ ...f, name: e.target.value }))}
          />
        </Field>
        <Field label="E-mail">
          <input
            className="input"
            type="email"
            value={form.email}
            onChange={(e) => setForm((f) => ({ ...f, email: e.target.value }))}
          />
        </Field>
        <Field label="Usuário (opcional)" hint="Login alternativo ao e-mail, ex.: naldoev.">
          <input
            className="input"
            value={form.username}
            onChange={(e) => setForm((f) => ({ ...f, username: e.target.value }))}
          />
        </Field>
        <Field
          label="Senha inicial"
          hint="Mínimo de 6 caracteres. Combine com a pessoa para trocar no primeiro acesso."
        >
          <input
            className="input"
            type="text"
            value={form.password}
            onChange={(e) => setForm((f) => ({ ...f, password: e.target.value }))}
          />
        </Field>
        <Field label="Papel">
          <select
            className="select"
            value={form.role}
            onChange={(e) => setForm((f) => ({ ...f, role: e.target.value as Role }))}
          >
            {ROLES.map((role) => (
              <option key={role} value={role}>
                {ROLE_LABEL[role]}
              </option>
            ))}
          </select>
        </Field>
        {error && (
          <p className="state-error" role="alert">
            {error}
          </p>
        )}
      </div>
    </Modal>
  );
}

function EditUserForm({
  user,
  onClose,
  onSaved,
}: {
  user: PublicUser;
  onClose: () => void;
  onSaved: () => void;
}) {
  const [form, setForm] = useState({
    name: user.name,
    email: user.email,
    username: user.username ?? '',
    googleEmail: user.googleEmail ?? '',
    password: '',
  });
  const [error, setError] = useState<string | null>(null);
  const [saving, setSaving] = useState(false);

  async function save() {
    setSaving(true);
    setError(null);
    try {
      const changes: Record<string, unknown> = {};
      if (form.name.trim() !== user.name) changes.name = form.name.trim();
      if (form.email.trim().toLowerCase() !== user.email) changes.email = form.email.trim();
      const nextUsername = form.username.trim() || null;
      if (nextUsername !== (user.username ?? null)) changes.username = nextUsername;
      const nextGoogleEmail = form.googleEmail.trim() || null;
      if (nextGoogleEmail !== (user.googleEmail ?? null)) changes.googleEmail = nextGoogleEmail;
      if (form.password) changes.password = form.password;

      if (Object.keys(changes).length === 0) {
        onClose();
        return;
      }
      await api.patch(`/users/${user.id}`, changes);
      onSaved();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Não foi possível salvar as alterações.');
      setSaving(false);
    }
  }

  return (
    <Modal
      title={`Editar ${user.name}`}
      onClose={onClose}
      footer={
        <>
          <button type="button" className="btn btn-ghost" onClick={onClose}>
            Cancelar
          </button>
          <button
            type="button"
            className="btn btn-primary"
            onClick={() => void save()}
            disabled={saving}
          >
            {saving ? 'Salvando…' : 'Salvar'}
          </button>
        </>
      }
    >
      <div className="stack">
        <Field label="Nome">
          <input
            className="input"
            value={form.name}
            onChange={(e) => setForm((f) => ({ ...f, name: e.target.value }))}
          />
        </Field>
        <Field label="E-mail">
          <input
            className="input"
            type="email"
            value={form.email}
            onChange={(e) => setForm((f) => ({ ...f, email: e.target.value }))}
          />
        </Field>
        <Field label="Usuário (opcional)" hint="Login alternativo ao e-mail.">
          <input
            className="input"
            value={form.username}
            onChange={(e) => setForm((f) => ({ ...f, username: e.target.value }))}
          />
        </Field>
        <Field
          label="Conta Google vinculada (opcional)"
          hint="E-mail da conta Google usada em 'Continuar com Google'. Deixe em branco para desvincular."
        >
          <input
            className="input"
            type="email"
            value={form.googleEmail}
            onChange={(e) => setForm((f) => ({ ...f, googleEmail: e.target.value }))}
          />
        </Field>
        <Field label="Nova senha (opcional)" hint="Deixe em branco para manter a senha atual.">
          <input
            className="input"
            type="text"
            value={form.password}
            onChange={(e) => setForm((f) => ({ ...f, password: e.target.value }))}
          />
        </Field>
        {error && (
          <p className="state-error" role="alert">
            {error}
          </p>
        )}
      </div>
    </Modal>
  );
}

function UserHistoryModal({ user, onClose }: { user: PublicUser; onClose: () => void }) {
  const history = useAsync(() => api.get<UserHistoryEntry[]>(`/users/${user.id}/history`), [user.id]);

  return (
    <Modal title={`Histórico — ${user.name}`} onClose={onClose} wide>
      {history.loading && <Loading label="Carregando histórico…" />}
      {history.error && <ErrorState message={history.error} onRetry={history.reload} />}
      {history.data && history.data.length === 0 && (
        <p className="text-muted text-label">Nenhuma alteração registrada ainda.</p>
      )}
      {history.data && history.data.length > 0 && (
        <table className="table">
          <thead>
            <tr>
              <th>Quando</th>
              <th>Campo</th>
              <th>De</th>
              <th>Para</th>
            </tr>
          </thead>
          <tbody>
            {history.data.map((entry) => (
              <tr key={entry.id}>
                <td className="text-muted tabular-nums">{formatDateTime(entry.createdAt)}</td>
                <td>{HISTORY_FIELD_LABEL[entry.field] ?? entry.field}</td>
                <td className="text-muted">{historyValueLabel(entry.field, entry.oldValue)}</td>
                <td>{historyValueLabel(entry.field, entry.newValue)}</td>
              </tr>
            ))}
          </tbody>
        </table>
      )}
    </Modal>
  );
}
